import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import useCollectorScroll from "@/hooks/collector/useCollectorScroll";
import { formatNumberWithDotAndComma } from "@/utils/moneyFormatter";
import api from "@/api/baseApi";
import toast from "react-hot-toast";
import { FileInput } from "@/components/ui/FileInput";
import { toWords } from "@/utils/TowordsConfig";

interface DepositMobileProps {
  onDeposit: () => void;
  refreshKey: number;
}

const DepositMobile: React.FC<DepositMobileProps> = ({
  onDeposit,
  refreshKey,
}) => {
  const [selectedCollections, setSelectedCollections] = useState<
    CollectionOrder[]
  >([]);
  const [open, setOpen] = useState(false);
  const [depositType, setDepositType] = useState("office");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [submitting, setSubmitting] = useState(false);
  const [reciept, setReciept] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const { collections, loading, loadMore, handleRefresh } = useCollectorScroll({
    perPage: 5,
  });

  const totalBalance = selectedCollections?.reduce(
    (acc, row) => acc + parseFloat(row.amount.toString()),
    0
  );
  const [amount, setAmount] = useState(totalBalance);
  const [amountLetter, setAmountLetter] = useState(
    toWords.convert(amount, { currency: true })
  );

  useEffect(() => {
    setAmount(totalBalance);
    setAmountLetter(toWords.convert(totalBalance, { currency: true }));
  }, [selectedCollections]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleRefresh();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const observer = useRef<IntersectionObserver | null>(null);
  const lastCollectionElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMore();
          }
        },
        { rootMargin: "0px 0px 200px 0px", threshold: 0.1 }
      );
      if (node) observer.current.observe(node);
    },
    [loading, loadMore]
  );

  // Handle selection of collections
  const handleCollectionSelect = (collection: CollectionOrder) => {
    setSelectedCollections((prevSelected) =>
      prevSelected.includes(collection)
        ? prevSelected.filter((selectedId) => selectedId !== collection)
        : [...prevSelected, collection]
    );
  };

  const handleDeposit = async () => {
    setSubmitting(true);

    let formData = new FormData();
    formData.append("image", reciept as File);
    formData.append("path", "collect-reciepts");
    formData.append("description", "deposited by collector ");
    await api
      .post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(async (res) => {
        console.log(res.data);
        const deposit = {
          collectionIds: selectedCollections.map((row) => row.id),
          depositedMethod: depositType,
          depositedType: paymentMethod,
          depositedBank: "bnp",
          status: "Payment Deposited",
          amountLetter: amountLetter,
          fileId: res.data.id.toString(),
        };
        await api
          .patch("/collector/orders/deposit", deposit)
          .then((res) => {
            console.log("res :", res);

            toast.success("Amount deposited successfully");
            onDeposit();
            setImagePreview(null);
            setAmountLetter("");
            setReciept(null);
            setSelectedCollections([]);

            setOpen(false);
          })
          .catch((error) => {
            console.log("err in deposit: ", error);
            toast.error("Something went wrong");
          });
      })
      .catch((error) => {
        console.log(error);
        toast.error("File upload failed");
      });

    setSubmitting(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setReciept(file);

    // Generate a preview URL if the file is an image
    if (file && file.type.startsWith("image/")) {
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    } else {
      setImagePreview(null);
    }
  };
  const handleRemoveFile = () => {
    setReciept(null);
    setImagePreview(null);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const amountValue = Number(e.target.value);
    setAmount(amountValue);

    // Convert amount to French words
    const amountInWords = toWords.convert(amount, { currency: true });
    setAmountLetter(amountInWords);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className="w-full text-[16px] bg-primary/20 border border-primary text-primary py-2  font-normal rounded-md hover:bg-primary/30 cursor-pointer transition ease-in-out duration-200">
        Deposit
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] h-[90%] overflow-y-scroll p-8">
        <DialogHeader>
          <DialogTitle className="text-xl">Deposit</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 pt-4">
          <div className="flex flex-col gap-4">
            <ToggleGroup
              type="single"
              value={depositType}
              onValueChange={(value) => value && setDepositType(value)}
              className="flex justify-between"
            >
              <ToggleGroupItem
                value="office"
                className="flex-1 border border-primary rounded-md data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                Office
              </ToggleGroupItem>
              <ToggleGroupItem
                value="bank"
                className="flex-1 border border-primary rounded-md data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                Bank
              </ToggleGroupItem>
            </ToggleGroup>

            <ToggleGroup
              type="single"
              value={paymentMethod}
              onValueChange={(value) => value && setPaymentMethod(value)}
              className="flex justify-between"
            >
              <ToggleGroupItem
                value="cash"
                className="flex-1 border border-primary rounded-md data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                Cash
              </ToggleGroupItem>
              <ToggleGroupItem
                value="cheque"
                className="flex-1 border border-primary rounded-md data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                Chéque
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {depositType === "bank" && (
            <div className="grid gap-2">
              <Label htmlFor="bank">Bank</Label>
              <Select>
                <SelectTrigger id="bank">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bnp">BNP Paribas</SelectItem>
                  <SelectItem value="societe">Société Générale</SelectItem>
                  <SelectItem value="credit">Crédit Agricole</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="balance">Total balance</Label>
            <Input
              disabled
              onChange={handleAmountChange}
              id="balance"
              value={amount}
              placeholder="35000 DZD"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="amount-letter">Deposit amount in letter</Label>
            <Input
              value={toWords.convert(amount, { currency: true })}
              onChange={(e) => setAmountLetter(e.target.value)}
              id="amount-letter"
              placeholder="Three hundred"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label
              htmlFor="receipt"
              className="text-left text-sm font-normal text-[#717171]"
            >
              Attach a receipt
            </Label>
            <FileInput id="reciept" onChange={handleFileChange} />
          </div>
          {imagePreview && (
            <div className="flex flex-col items-center mt-2 space-y-2">
              <img
                src={imagePreview}
                alt="File preview"
                className="object-cover w-32 h-32 rounded"
              />
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-sm text-red-500 underline hover:text-red-700"
              >
                Remove
              </button>
            </div>
          )}

          <div className="flex flex-col gap-2 h-[200px] overflow-y-scroll">
            {collections.map((collection, index) => (
              <div
                key={collection.id}
                ref={
                  index === collections.length - 1
                    ? lastCollectionElementRef
                    : null
                }
                className="flex items-center gap-2 shadow-md p-2 rounded-[10px] hover:shadow-lg cursor-pointer"
              >
                {collection.status === "Payment Collected" && (
                  <input
                    className="w-4 h-4"
                    type="checkbox"
                    checked={selectedCollections.includes(collection as any)}
                    onChange={() => handleCollectionSelect(collection as any)}
                  />
                )}
                <div className="w-full flex items-center">
                  <div className="w-[50%]">
                    <p className="text-base font-medium truncate">
                      {collection.Customer?.commercial_name}
                    </p>
                    <p className="text-[#979797] text-xs">ID{collection.id}</p>
                  </div>
                  <div className="flex items-center gap-1 w-[50%]">
                    <p className="text-[#848382] text-xs font-normal">
                      Amount:
                    </p>
                    <span className="text-primary font-bold text-xs">
                      {formatNumberWithDotAndComma(
                        collection.amount.toString()
                      )}{" "}
                      DZD
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button
            disabled={submitting}
            onClick={handleDeposit}
            className="w-full py-4 mt-8 bg-[#4CAF50] hover:bg-[#45a049]"
          >
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <span>Deposit</span>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DepositMobile;
